import React, { useState } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, LinearProgress, Box, Typography } from "@mui/material";
import {
  Edit,
  Delete,
  FlagIcon as Flag,
  Done as CheckCircle,
  Schedule,
  Warning,
} from "components/icons";
import { parseLocalDate, startOfLocalDay } from "utils/dateUtil";

const getStatusColor = (status) => {
  const colors = {
    PENDING: "default",
    IN_PROGRESS: "info",
    COMPLETED: "success",
    DELAYED: "error",
    ON_HOLD: "warning",
  };
  return colors[status] || "default";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "COMPLETED": return <CheckCircle />;
    case "DELAYED": return <Warning />;
    case "IN_PROGRESS": return <Schedule />;
    default: return <Flag />;
  }
};

// Overdue means the due day has passed, not that an instant has. Comparing a date-only string
// against new Date() asked the instant question: the string parses as UTC midnight, so in IST a
// milestone read as overdue from 05:30 on its own due date.
const isOverdue = (dueDate, status) => {
  if (status === "COMPLETED") return false;
  return Boolean(dueDate) && startOfLocalDay(dueDate) < startOfLocalDay(new Date());
};

export const MilestoneDataGrid = ({ milestones, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 70 },
    { field: "milestoneName", headerName: "Milestone", width: 200, flex: 1 },
    { field: "projectName", headerName: "Project", width: 150 },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 120,
      renderCell: (params) => {
        const overdue = isOverdue(params.value, params.row.status);
        return (
          <Chip
            label={params.value ? parseLocalDate(params.value).toLocaleDateString() : "N/A"}
            color={overdue ? "error" : "default"}
            size="small"
            variant={overdue ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value?.replace("_", " ")}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "completionPercentage",
      headerName: "Completion",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ width: "100%", mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={params.value || 0}
              color={params.value >= 100 ? "success" : "primary"}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">{`${Math.round(params.value || 0)}%`}</Typography>
        </Box>
      ),
    },
    { field: "owner", headerName: "Owner", width: 130 },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={milestones || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.milestoneId}
      />
    </MDBox>
  );
};

MilestoneDataGrid.propTypes = {
  milestones: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default MilestoneDataGrid;

